/**
 * remote-vfx.config.ts — fallback timings + effect-id defaults for the
 * remote ability-VFX consumer (Lane 5 of the VFX & Rendering Parity arc).
 *
 * These mirror the constants the LOCAL caster path bakes in:
 *  - AOEBehavior emits its zone VFX with `effectId: vfx.castEffect || 'aoe_blast'`
 *    (TomlAbilityFactory.ts AoE branch) and a `duration` derived from the
 *    cast timeline. The remote side has no timeline, so we fall back to a
 *    fixed zone duration here.
 *  - withBuff plays `vfx.castEffect || 'defensive_aura'` for self/buff
 *    abilities; the aura colour comes from `vfx.auraColor` (or the school).
 *
 * Keeping these literals here (not inline) so a balance pass can retune the
 * remote-zone feel without touching resolution logic. PARITY rule: the
 * default *effect ids* MUST match the local fallbacks above.
 */

export interface RemoteVfxConfig {
  /** Fallback zone effect id when an AoE/ground ability has no `castEffect`. Matches AOEBehavior. */
  aoeZoneFallbackEffectId: string;
  /** Fallback aura effect id when a self/buff ability has no `castEffect`. Matches withBuff. */
  auraFallbackEffectId: string;
  /** Zone-effect lifetime (ms) on remote clients — local derives this from the
   *  cast timeline; the remote consumer has no timeline so it uses this. */
  zoneDurationMs: number;
  /** Default radius (px) when an AoE ability omits `radius`. Matches AOEBehavior's `data.radius ?? 120`. */
  defaultAoeRadius: number;
  /** Plan 29 F5: most remote cast plans (swings, areas, zones, cones) drawing
   *  at once on this screen; a cast past the cap is dropped, not queued. */
  maxActiveRemoteCasts: number;
  /** A remote cast whose drawn reach is this far (px) outside the camera view is not drawn. */
  remoteCullMarginPx: number;
  /** Shortest lifetime (ms) a remote plan holds a budget slot (an instant swing still costs). */
  remoteMinSlotMs: number;
}

export const REMOTE_VFX_CONFIG: RemoteVfxConfig = {
  aoeZoneFallbackEffectId: 'aoe_blast',
  auraFallbackEffectId: 'defensive_aura',
  zoneDurationMs: 1500,
  defaultAoeRadius: 120,
  maxActiveRemoteCasts: 10,
  remoteCullMarginPx: 96,
  remoteMinSlotMs: 250,
};
